/**
 * 收银端的本地偏好（`localStorage`）。
 *
 * 三件事放同一处，是因为它们天然成组：握手要靠 `deviceId`（§19.1）、
 * 静音开关要在刷新后保持（§6.2）、当前地点决定了房间与所有取数（§1.6 门店/展会模式）。
 * 分成三个模块各读写各的 key，迟早出现「谁负责写」的空白。
 * Phase 9 在这个组里加了第四个键：**当前地点的地点码**（离线验单要用，见 `LOCATION_CODE_KEY`）。
 *
 * ⚠️ **静音开关本 Phase 只落本地**：§6.2 要求「写回 `newOrderSound`」，而契约 §7.7 的
 * `PUT /locations/:id/settings/:key` 白名单只有 `wechatQrUrl` / `alipayQrUrl` /
 * `fullscreenAdEnabled` —— `newOrderSound` 不在里面，写它必然 400 `SETTING_KEY_INVALID`。
 * 不静默吞掉这个 400，也不假装写成功：本 Phase 写本地，跨设备的开关归靠 Chain A 扩白名单
 * （见计划末「接口冻结冲突」第 3 条）。
 *
 * **四个键名写死（进测试，不许在别处再拼一遍）**：
 *
 * | 键 | 值 | 用途 |
 * |---|---|---|
 * | `cuito-cashier-device` | UUID（首次生成后**永不变**） | §19.1 握手 `deviceId`；`cashier:<deviceId>` 房间 |
 * | `cuito-cashier-location` | 地点 id，或键不存在 | 当前收银地点（模式徽标 / 房间 / 全部取数） |
 * | `cuito-cashier-sound` | `'true'` / `'false'` | §6.2 的新订单提示音开关 |
 * | `cuito-cashier-location-code` | 地点码（`StockLocation.code`） | §20.7 第 8 步的**离线**地点校验（Phase 9） |
 *
 * ⚠️ 本文件的位置（`src/cashier/lib/`）取自计划 Task 13 的文件表与 Chain C 全部页面的
 * `'../lib/prefs'` 引用；§3.4 表里那一行写的 `src/lib/prefs.ts` 是同一件事的另一个路径，
 * 两处不可能同时对（已登记在交付报告里）。
 */
const DEVICE_KEY = 'cuito-cashier-device';
const LOCATION_KEY = 'cuito-cashier-location';
const SOUND_KEY = 'cuito-cashier-sound';

/**
 * 当前地点的**地点码**（`StockLocation.code`）。
 *
 * Phase 9 追加的第四个键（`cuito-cashier-location-code`），用途只有一个：
 * 订单码里的 `locationCode`（契约 §20.7 第 8 步「校验订单归属地点与当前收银地点一致」）
 * 在**离线**时必须有一个本机就能读到的对照物。地点码来自 `GET /locations`，
 * 而那条请求断网时只能靠 SW 缓存 —— 把「本机在哪个店」这件事实单独存一份，
 * 是离线校验能成立的前提（`CashierLayout` 在地点解析出来时写，扫码页离线解构时读）。
 *
 * 值随地点切换而更新，未选地点时被删掉：留着一个旧地点的码，会让「不属于本店」的判定
 * 拿着一张**上一个店**的码去比 —— 那是比不校验更坏的一种失败（看起来验过了）。
 */
const LOCATION_CODE_KEY = 'cuito-cashier-location-code';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function storageOrNull(storage?: StorageLike): StorageLike | null {
  if (storage) return storage;
  // SSR/测试环境可能没有 localStorage；缺了它不该让收银端整页崩掉
  return typeof window === 'undefined' ? null : window.localStorage;
}

/** 设备标识（§19.1 的 `deviceId`）：首次调用生成并持久化，此后恒定 —— 定向回执 `cashier:<deviceId>` 靠它 */
export function readDeviceId(storage?: StorageLike): string {
  const store = storageOrNull(storage);
  if (store === null) return 'cashier-unknown';
  const existing = store.getItem(DEVICE_KEY);
  if (existing !== null && existing !== '') return existing;
  const created = crypto.randomUUID();
  store.setItem(DEVICE_KEY, created);
  return created;
}

/** 当前收银地点；`null` = 还没选（骨架会显示「未选择地点」并且不连 socket） */
export function readCashierLocationId(storage?: StorageLike): string | null {
  const value = storageOrNull(storage)?.getItem(LOCATION_KEY) ?? null;
  return value === '' ? null : value;
}

export function writeCashierLocationId(locationId: string | null, storage?: StorageLike): void {
  const store = storageOrNull(storage);
  if (store === null) return;
  if (locationId === null) store.removeItem(LOCATION_KEY);
  else store.setItem(LOCATION_KEY, locationId);
}

/** 新订单提示音开关（§6.2）：默认开；关掉时静音态是 `text-gray-400` */
export function readSoundEnabled(storage?: StorageLike): boolean {
  const raw = storageOrNull(storage)?.getItem(SOUND_KEY) ?? null;
  return raw !== 'false';
}

export function writeSoundEnabled(enabled: boolean, storage?: StorageLike): void {
  storageOrNull(storage)?.setItem(SOUND_KEY, enabled ? 'true' : 'false');
}

/**
 * 当前地点的地点码；`null` = 本机还不知道（还没选地点 / 从没成功拉过 `GET /locations`）。
 *
 * `null` 与「码不匹配」是两件事：前者**不做**离线地点校验（拿不到对照物就不能说人家错），
 * 后者拒绝接单（契约 §20.7 第 8 步）。把两者混成一句「不属于本店」会让刚装好的机器
 * 拒掉本店全部的码。
 */
export function readCashierLocationCode(storage?: StorageLike): string | null {
  const value = storageOrNull(storage)?.getItem(LOCATION_CODE_KEY) ?? null;
  return value === '' ? null : value;
}

export function writeCashierLocationCode(locationCode: string | null, storage?: StorageLike): void {
  const store = storageOrNull(storage);
  if (store === null) return;
  if (locationCode === null) store.removeItem(LOCATION_CODE_KEY);
  else store.setItem(LOCATION_CODE_KEY, locationCode);
}
